/**
 * A custom HTML element that displays a job salary with currency formatting.
 *
 * @class JobSalary
 * @extends {HTMLElement}
 *
 * @property {string} currencyCode - The currency code (e.g., 'USD').
 * @property {string} currencySymbol - The currency symbol (e.g., '$').
 * @property {number} value - The salary value.
 *
 * @example
 * <job-salary currency-code="EUR" currency-symbol="€" value="50000"></job-salary>
 *
 * @method connectedCallback - Called when the element is added to the DOM.
 * @method static get observedAttributes - Returns an array of attributes to observe for changes.
 * @method attributeChangedCallback - Called when one of the observed attributes changes.
 * @method render - Renders the formatted salary inside the element.
 */
class JobSalary extends HTMLElement {
  constructor() {
    super();
    this.currencyCode = this.getAttribute("currency-code") || "USD";
    this.currencySymbol = this.getAttribute("currency-symbol") || "$";
    this.value = parseFloat(this.getAttribute("value") || "0");
  }

  connectedCallback() {
    this.render();
  }

  static get observedAttributes() {
    return ["currency-code", "currency-symbol", "value"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      switch (name) {
        case "currency-code":
          this.currencyCode = newValue;
          break;
        case "currency-symbol":
          this.currencySymbol = newValue;
          break;
        case "value":
          this.value = parseFloat(newValue);
          break;
      }
      this.render();
    }
  }

  render() {
    const formattedValue = new Intl.NumberFormat([], {
      style: "currency",
      currency: this.currencyCode,
      currencyDisplay: "symbol",
    }).format(this.value);

    const displaySymbol = !formattedValue.includes(this.currencySymbol);

    this.innerHTML = displaySymbol
      ? `<span>${this.currencySymbol} ${formattedValue}</span>`
      : `
            <span>${formattedValue}</span>
        `;
  }
}

customElements.define("job-salary", JobSalary);
